import { useRef, useState } from "react";
import { useTheme } from "@mui/material";
import { LuVideo } from "react-icons/lu";
import { INTEGRATION_STATUS } from "../../utils/interviewApi";

const OPTIONS = [
  { value: "google", label: "Google Meet", description: "Join via Google Meet" },
  { value: "microsoft", label: "Microsoft Teams", description: "Join via Microsoft Teams" },
];

export default function MeetTypePicker({ value, onChange, labelledBy, options, accounts = {}, statuses = {} }) {
  const theme = useTheme();
  const buttonRefs = useRef([]);
  const [hovered, setHovered] = useState(null);
  const choices = (options || OPTIONS.map((option) => option.value))
    .map((id) => OPTIONS.find((option) => option.value === id))
    .filter(Boolean);
  const isUnavailable = (id) => {
    const status = statuses[id];
    return Boolean(status) && status !== INTEGRATION_STATUS.CONNECTED;
  };
  const selectedIndex = choices.findIndex((option) => option.value === value);
  const activeIndex = selectedIndex === -1 ? 0 : selectedIndex;

  const move = (event, direction) => {
    event.preventDefault();
    const next = (activeIndex + direction + choices.length) % choices.length;
    if (isUnavailable(choices[next].value)) return;
    onChange(choices[next].value);
    buttonRefs.current[next]?.focus();
  };

  return (
    <div role="radiogroup" aria-labelledby={labelledBy} className="grid grid-cols-2 gap-2">
      {choices.map((option, index) => {
        const unavailable = isUnavailable(option.value);
        const selected = option.value === value;
        // One line: the connected account, when known, follows the name
        const account = (accounts[option.value] || "").trim();
        return (
          <button
            key={option.value}
            ref={(node) => {
              buttonRefs.current[index] = node;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={unavailable}
            tabIndex={index === activeIndex ? 0 : -1}
            onClick={() => {
              if (unavailable) return;
              onChange(option.value);
            }}
            onMouseEnter={() => setHovered(option.value)}
            onMouseLeave={() => setHovered((current) => (current === option.value ? null : current))}
            onKeyDown={(event) => {
              if (event.key === "ArrowRight" || event.key === "ArrowDown") move(event, 1);
              if (event.key === "ArrowLeft" || event.key === "ArrowUp") move(event, -1);
            }}
            className="flex h-full min-h-[48px] items-center gap-2.5 rounded-lg border px-3.5 py-2 text-left text-sm leading-tight transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              borderColor: selected ? theme.palette.info.main : theme.palette.secondary.v4,
              backgroundColor: selected ? "#E8EEF8" : hovered === option.value ? "#F7F7F7" : "#ffffff",
              color: selected ? theme.palette.info.main : "#111111",
              fontWeight: selected ? 600 : 500,
              outlineColor: theme.palette.info.main,
              boxShadow: selected ? `inset 0 0 0 1px ${theme.palette.info.main}` : "none",
            }}
          >
            <LuVideo className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} aria-hidden="true" />
            <span className="min-w-0 flex items-baseline gap-1.5" title={account || option.description}>
              <span className="shrink-0">{option.label}</span>
              {account && (
                <span className="truncate text-xs font-normal" style={{ color: theme.palette.text.secondary }}>
                  {account}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}
